import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { dbToShopifyProduct, DbProductRow, DB_PRODUCT_SELECT } from "@/lib/dbProductMapper";
import type { ShopifyProduct } from "@/lib/shopify";

interface AnimalSection {
  tag: string;
  excludeL2?: string[];
  includeL2?: string[];
}

const ANIMAL_SECTIONS: AnimalSection[] = [
  { tag: "hund", excludeL2: ["L2:vard-halsa"] },
  { tag: "katt", excludeL2: ["L2:vard-halsa"] },
  { tag: "smadjur" },
  { tag: "fagel" },
  { tag: "reptil" },
  { tag: "akvarium" },
];

export interface HomepageData {
  popular: ShopifyProduct[];
  newArrivals: ShopifyProduct[];
  animals: Record<string, ShopifyProduct[]>;
}

const toProducts = (rows: unknown[]) =>
  (rows || []).map((r) => dbToShopifyProduct(r as unknown as DbProductRow));

/**
 * Phase 1: Only popular + newArrivals (2 queries instead of 8).
 * This is what's needed above the fold.
 */
async function fetchAboveFoldData(): Promise<Pick<HomepageData, "popular" | "newArrivals">> {
  const [popularRes, newArrivalsRes] = await Promise.all([
    supabase
      .from("shopify_products")
      .select(DB_PRODUCT_SELECT)
      .eq("available_for_sale", true)
      .eq("status", "active")
      .gt("inventory_quantity", 0)
      .not("image_url", "is", null)
      .order("updated_at", { ascending: false })
      .limit(8),
    supabase
      .from("shopify_products")
      .select(DB_PRODUCT_SELECT)
      .eq("available_for_sale", true)
      .eq("status", "active")
      .gt("inventory_quantity", 0)
      .not("image_url", "is", null)
      .order("shopify_created_at", { ascending: false })
      .limit(4),
  ]);

  return {
    popular: toProducts(popularRes.data || []),
    newArrivals: toProducts(newArrivalsRes.data || []),
  };
}

/**
 * Phase 2: Animal sections (6 queries) — deferred, loaded after above-fold is ready.
 */
async function fetchAnimalData(): Promise<Record<string, ShopifyProduct[]>> {
  const animalResults = await Promise.all(
    ANIMAL_SECTIONS.map((section) =>
      supabase
        .from("shopify_products")
        .select(DB_PRODUCT_SELECT)
        .eq("available_for_sale", true)
        .eq("status", "active")
        .contains("tags", [`L1:${section.tag}`])
        .gt("inventory_quantity", 0)
        .not("image_url", "is", null)
        .order("updated_at", { ascending: false })
        .limit(24)
    )
  );

  const animals: Record<string, ShopifyProduct[]> = {};
  ANIMAL_SECTIONS.forEach((section, i) => {
    let rows = (animalResults[i].data || []) as Record<string, unknown>[];
    if (section.includeL2?.length || section.excludeL2?.length) {
      rows = rows.filter((p) => {
        const tags = (p.tags as string[]) || [];
        if (section.includeL2?.length && !section.includeL2.some((l2) => tags.includes(l2))) return false;
        if (section.excludeL2?.length && section.excludeL2.some((l2) => tags.includes(l2))) return false;
        return true;
      });
    }
    rows.sort((a, b) => {
      const aTop = ((a.tags as string[]) || []).includes("toppsäljare") ? 1 : 0;
      const bTop = ((b.tags as string[]) || []).includes("toppsäljare") ? 1 : 0;
      return bTop - aTop;
    });
    animals[section.tag] = toProducts(rows.slice(0, 4));
  });

  return animals;
}

export function useHomepageData() {
  const aboveFold = useQuery({
    queryKey: ["homepage-above-fold"],
    queryFn: fetchAboveFoldData,
    staleTime: 5 * 60 * 1000,
  });

  const animalData = useQuery({
    queryKey: ["homepage-animals"],
    queryFn: fetchAnimalData,
    staleTime: 5 * 60 * 1000,
    // Only start loading animal data after above-fold data is ready
    enabled: aboveFold.isSuccess,
  });

  return {
    data: aboveFold.data
      ? {
          popular: aboveFold.data.popular,
          newArrivals: aboveFold.data.newArrivals,
          animals: animalData.data || ({} as Record<string, ShopifyProduct[]>),
        }
      : undefined,
    isLoading: aboveFold.isLoading,
  };
}
